"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBreakpoint } from "react-use";
import type { DropInteractionParams } from "./Drop";
import { DropLocation } from "./Drop";
import type { BoostAnimationState } from "./DropBoostAnimation";
import DropBoostAnimation from "./DropBoostAnimation";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
enum GroupingThreshold {
  TIME_DIFFERENCE = 60000,
}
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GroupingThreshold.TIME_DIFFERENCE;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: "tw-border-l tw-border-[#E8D48A]/40 tw-border-y-0 tw-border-r-0",
  2: "tw-border-l tw-border-[#DDDDDD]/40 tw-border-y-0 tw-border-r-0",
  3: "tw-border-l tw-border-[#CD7F32]/40 tw-border-y-0 tw-border-r-0",
  default: "tw-border-l tw-border-iron-600/40 tw-border-y-0 tw-border-r-0"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-border-l tw-border-l-[#3CCB7F] tw-border-solid tw-border-y-0 tw-border-r-0";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = isWaveView ? "" : "tw-ring-1 tw-ring-inset tw-ring-iron-800";
    const bgClass = isWaveView ? "" : "tw-bg-iron-950";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES];
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly wrapContentOnly?: ((content: React.ReactNode) => React.ReactNode) | undefined;
}
const WaveDrop = t0 => {
  const $ = _c(153);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onQuote,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    wrapContentOnly
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [boostAnimation, setBoostAnimation] = useState(null);
  const dropRef = useRef(null);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  let t1;
  if ($[0] !== drop || $[1] !== isDrop || $[2] !== previousDrop) {
    t1 = !isDrop && shouldGroupWithDrop(drop, previousDrop);
    $[0] = drop;
    $[1] = isDrop;
    $[2] = previousDrop;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldGroupWithPreviousDrop = t1;
  let t2;
  if ($[4] !== drop || $[5] !== isDrop || $[6] !== nextDrop) {
    t2 = !isDrop && shouldGroupWithDrop(drop, nextDrop);
    $[4] = drop;
    $[5] = isDrop;
    $[6] = nextDrop;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shouldGroupWithNextDrop = t2;
  const isMobile = useIsMobileDevice();
  const hasTouch = useHasTouchInput() || isMobile;
  const breakpoint = useBreakpoint();
  const isMdUp = breakpoint === "MD";
  const allowLongPress = hasTouch && !isMdUp;
  const compact = useCompactMode();
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = !shouldGroupWithPreviousDrop || isProfileView;
  let t3;
  if ($[8] !== isProfileView || $[9] !== shouldGroupWithNextDrop || $[10] !== shouldGroupWithPreviousDrop) {
    t3 = () => {
      if (isProfileView) {
        return "tw-py-4";
      }
      if (shouldGroupWithPreviousDrop) {
        return "tw-pt-1";
      }
      if (shouldGroupWithNextDrop) {
        return "tw-pt-4 tw-pb-1";
      }
      return "tw-py-4";
    };
    $[8] = isProfileView;
    $[9] = shouldGroupWithNextDrop;
    $[10] = shouldGroupWithPreviousDrop;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getGroupingClass = t3;
  const groupingClass = getGroupingClass();
  let t4;
  if ($[12] !== allowLongPress || $[13] !== dispatch || $[14] !== editingDropId) {
    t4 = () => {
      if (!allowLongPress) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[12] = allowLongPress;
    $[13] = dispatch;
    $[14] = editingDropId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleLongPress = t4;
  let t5;
  if ($[16] !== allowLongPress || $[17] !== handleLongPress || $[18] !== isEditing) {
    t5 = e => {
      if (!allowLongPress) {
        return;
      }
      if (isEditing) {
        return;
      }
      const touch = e.touches[0];
      touchStartPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      longPressTimeoutRef.current = setTimeout(handleLongPress, 500);
    };
    $[16] = allowLongPress;
    $[17] = handleLongPress;
    $[18] = isEditing;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      touchStartPosition.current = null;
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[21] !== allowLongPress) {
    t7 = e_0 => {
      if (!allowLongPress) {
        return;
      }
      if (!touchStartPosition.current) {
        return;
      }
      const touch_0 = e_0.touches[0];
      const deltaX = Math.abs(touch_0.clientX - touchStartPosition.current.x);
      const deltaY = Math.abs(touch_0.clientY - touchStartPosition.current.y);
      if ((deltaX > 10 || deltaY > 10) && longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    $[21] = allowLongPress;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleTouchMove = t7;
  let t8;
  if ($[23] !== activePartIndex || $[24] !== dispatch || $[25] !== drop || $[26] !== editingDropId || $[27] !== onReply) {
    t8 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[23] = activePartIndex;
    $[24] = dispatch;
    $[25] = drop;
    $[26] = editingDropId;
    $[27] = onReply;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleOnReply = t8;
  let t9;
  if ($[29] !== activePartIndex || $[30] !== dispatch || $[31] !== drop || $[32] !== editingDropId || $[33] !== onQuote) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onQuote({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[29] = activePartIndex;
    $[30] = dispatch;
    $[31] = drop;
    $[32] = editingDropId;
    $[33] = onQuote;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const handleOnQuote = t9;
  let t10;
  if ($[35] !== dispatch || $[36] !== editingDropId) {
    t10 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[35] = dispatch;
    $[36] = editingDropId;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_1 => {
      e_1.stopPropagation();
      setLongPressTriggered(false);
      setIsSlideUp(true);
    };
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const handleOpenTouchActions = t11;
  let t12;
  if ($[39] !== dispatch || $[40] !== drop.id) {
    t12 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[39] = dispatch;
    $[40] = drop.id;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const handleOnEdit = t12;
  let t13;
  if ($[42] !== activePartIndex || $[43] !== dispatch || $[44] !== drop || $[45] !== dropUpdateMutation) {
    t13 = (newContent, mentions) => {
      const cleanedMentions = (mentions ?? drop.mentioned_users).map(_temp);
      const updateRequest = {
        parts: drop.parts.map((part, index) => ({
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop ?? null,
          media: part.media
        })),
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[42] = activePartIndex;
    $[43] = dispatch;
    $[44] = drop;
    $[45] = dropUpdateMutation;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const handleEditSave = t13;
  let t14;
  if ($[47] !== dispatch) {
    t14 = () => {
      dispatch(setEditingDropId(null));
    };
    $[47] = dispatch;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const handleEditCancel = t14;
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setBoostAnimation(null);
    };
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const handleBoostAnimationComplete = t15;
  let t16;
  if ($[50] !== drop.context_profile_context?.boosted || $[51] !== drop.id) {
    t16 = () => {
      if (!dropRef.current) {
        return;
      }
      const rect = dropRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const isBoosted = drop.context_profile_context?.boosted ?? false;
      setBoostAnimation({
        id: `${drop.id}-${Date.now()}`,
        x,
        y,
        type: isBoosted ? "unboost" : "boost"
      });
    };
    $[50] = drop.context_profile_context?.boosted;
    $[51] = drop.id;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  drop.context_profile_context?.boosted;
  const handleMobileBoostAnimation = t16;
  let t17;
  let t18;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    t18 = [];
    $[53] = t17;
    $[54] = t18;
  } else {
    t17 = $[53];
    t18 = $[54];
  }
  useEffect(t17, t18);
  const effectiveIsSlideUp = isSlideUp && !isEditing;
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  let t19;
  if ($[55] !== drop.author || $[56] !== drop.reply_to || $[57] !== drop.wave || $[58] !== dropViewDropId || $[59] !== onReplyClick || $[60] !== previousDrop?.author?.handle || $[61] !== previousDrop?.reply_to?.drop_id) {
    t19 = drop.reply_to && (drop.reply_to.drop_id !== previousDrop?.reply_to?.drop_id || drop.author.handle !== previousDrop?.author?.handle) && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[55] = drop.author;
    $[56] = drop.reply_to;
    $[57] = drop.wave;
    $[58] = dropViewDropId;
    $[59] = onReplyClick;
    $[60] = previousDrop?.author?.handle;
    $[61] = previousDrop?.reply_to?.drop_id;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== drop || $[64] !== showAuthorInfo) {
    t20 = showAuthorInfo && <WaveDropAuthorPfp drop={drop} />;
    $[63] = drop;
    $[64] = showAuthorInfo;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const t21 = showAuthorInfo ? "calc(100% - 3.5rem)" : "100%";
  let t22;
  if ($[66] !== t21) {
    t22 = {
      maxWidth: t21
    };
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== activePartIndex || $[69] !== drop || $[70] !== handleOpenTouchActions || $[71] !== hasTouch || $[72] !== isEditing || $[73] !== isStorm || $[74] !== showAuthorInfo || $[75] !== showReplyAndQuote || $[76] !== showWaveInfo) {
    t23 = showAuthorInfo && <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} showActionsButton={hasTouch && showReplyAndQuote && !isEditing} onOpenActions={handleOpenTouchActions} />;
    $[68] = activePartIndex;
    $[69] = drop;
    $[70] = handleOpenTouchActions;
    $[71] = hasTouch;
    $[72] = isEditing;
    $[73] = isStorm;
    $[74] = showAuthorInfo;
    $[75] = showReplyAndQuote;
    $[76] = showWaveInfo;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  const t24 = `tw-w-full ${shouldGroupWithPreviousDrop && !isProfileView ? "tw-pl-[3.25rem]" : ""}`;
  let t25;
  if ($[78] !== activePartIndex || $[79] !== allowLongPress || $[80] !== drop || $[81] !== dropUpdateMutation.isPending || $[82] !== handleEditCancel || $[83] !== handleEditSave || $[84] !== handleLongPress || $[85] !== isEditing || $[86] !== onDropContentClick || $[87] !== onQuoteClick) {
    t25 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isEditing={isEditing} isSaving={dropUpdateMutation.isPending} onSave={handleEditSave} onCancel={handleEditCancel} hasTouch={allowLongPress} />;
    $[78] = activePartIndex;
    $[79] = allowLongPress;
    $[80] = drop;
    $[81] = dropUpdateMutation.isPending;
    $[82] = handleEditCancel;
    $[83] = handleEditSave;
    $[84] = handleLongPress;
    $[85] = isEditing;
    $[86] = onDropContentClick;
    $[87] = onQuoteClick;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== t24 || $[90] !== t25) {
    t26 = <div className={t24}>{t25}</div>;
    $[89] = t24;
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] !== t22 || $[93] !== t23 || $[94] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-1" style={t22}>{t23}{t26}</div>;
    $[92] = t22;
    $[93] = t23;
    $[94] = t26;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] !== t20 || $[97] !== t27) {
    t28 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t20}{t27}</div>;
    $[96] = t20;
    $[97] = t27;
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  let t29;
  if ($[99] !== activePartIndex || $[100] !== drop || $[101] !== handleOnEdit || $[102] !== handleOnQuote || $[103] !== handleOnReply || $[104] !== isEditing || $[105] !== isMobile || $[106] !== showReplyAndQuote) {
    t29 = !isMobile && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onQuote={handleOnQuote} onEdit={handleOnEdit} />;
    $[99] = activePartIndex;
    $[100] = drop;
    $[101] = handleOnEdit;
    $[102] = handleOnQuote;
    $[103] = handleOnReply;
    $[104] = isEditing;
    $[105] = isMobile;
    $[106] = showReplyAndQuote;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  let t30;
  if ($[108] !== t19 || $[109] !== t28 || $[110] !== t29) {
    t30 = <>{t19}{t28}{t29}</>;
    $[108] = t19;
    $[109] = t28;
    $[110] = t29;
    $[111] = t30;
  } else {
    t30 = $[111];
  }
  const contentBlock = t30;
  const t31 = `tw-mx-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${compact ? "tw-ml-11 tw-w-[calc(100%-2.5rem)]" : "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]"}`;
  let t32;
  if ($[112] !== drop.metadata) {
    t32 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[112] = drop.metadata;
    $[113] = t32;
  } else {
    t32 = $[113];
  }
  let t33;
  if ($[114] !== drop) {
    t33 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[114] = drop;
    $[115] = t33;
  } else {
    t33 = $[115];
  }
  let t34;
  if ($[116] !== drop) {
    t34 = <WaveDropReactions drop={drop} />;
    $[116] = drop;
    $[117] = t34;
  } else {
    t34 = $[117];
  }
  let t35;
  if ($[118] !== t31 || $[119] !== t32 || $[120] !== t33 || $[121] !== t34) {
    t35 = <div className={t31}>{t32}{t33}{t34}</div>;
    $[118] = t31;
    $[119] = t32;
    $[120] = t33;
    $[121] = t34;
    $[122] = t35;
  } else {
    t35 = $[122];
  }
  const reactionsRow = t35;
  const t36 = `${isDrop && location === DropLocation.WAVE ? "tw-px-4 tw-py-0.5" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  let t37;
  if ($[123] !== contentBlock || $[124] !== wrapContentOnly) {
    t37 = wrapContentOnly ? wrapContentOnly(contentBlock) : contentBlock;
    $[123] = contentBlock;
    $[124] = wrapContentOnly;
    $[125] = t37;
  } else {
    t37 = $[125];
  }
  let t38;
  if ($[126] !== drop || $[127] !== effectiveIsSlideUp || $[128] !== handleMobileBoostAnimation || $[129] !== handleOnAddReaction || $[130] !== handleOnEdit || $[131] !== handleOnQuote || $[132] !== handleOnReply || $[133] !== longPressTriggered || $[134] !== showReplyAndQuote) {
    t38 = <WaveDropMobileMenu drop={drop} isOpen={effectiveIsSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onQuote={handleOnQuote} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} onBoostAnimation={handleMobileBoostAnimation} />;
    $[126] = drop;
    $[127] = effectiveIsSlideUp;
    $[128] = handleMobileBoostAnimation;
    $[129] = handleOnAddReaction;
    $[130] = handleOnEdit;
    $[131] = handleOnQuote;
    $[132] = handleOnReply;
    $[133] = longPressTriggered;
    $[134] = showReplyAndQuote;
    $[135] = t38;
  } else {
    t38 = $[135];
  }
  let t39;
  if ($[136] !== boostAnimation || $[137] !== handleBoostAnimationComplete) {
    t39 = <DropBoostAnimation animation={boostAnimation} onComplete={handleBoostAnimationComplete} />;
    $[136] = boostAnimation;
    $[137] = handleBoostAnimationComplete;
    $[138] = t39;
  } else {
    t39 = $[138];
  }
  let t40;
  if ($[139] !== drop.serial_no || $[140] !== drop.stableHash || $[141] !== dropClasses || $[142] !== handleTouchEnd || $[143] !== handleTouchMove || $[144] !== handleTouchStart || $[145] !== reactionsRow || $[146] !== t37 || $[147] !== t38 || $[148] !== t39) {
    t40 = <div ref={dropRef} className={dropClasses} data-wave-drop-id={drop.stableHash} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t37}{reactionsRow}{t38}{t39}</div>;
    $[139] = drop.serial_no;
    $[140] = drop.stableHash;
    $[141] = dropClasses;
    $[142] = handleTouchEnd;
    $[143] = handleTouchMove;
    $[144] = handleTouchStart;
    $[145] = reactionsRow;
    $[146] = t37;
    $[147] = t38;
    $[148] = t39;
    $[149] = t40;
  } else {
    t40 = $[149];
  }
  let t41;
  if ($[150] !== t36 || $[151] !== t40) {
    t41 = <div className={t36}>{t40}</div>;
    $[150] = t36;
    $[151] = t40;
    $[152] = t41;
  } else {
    t41 = $[152];
  }
  return t41;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}